// @flow
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import t2 from "./locale"
import FieldSet from '../../field-set';
import Checkbox from '../../../basic-components/checkbox';
import Input from '../../../basic-components/input';
import { getCurrencySymbol } from '../../../utils/countries';
import ShippingPacketPrices from './shipping-packet-prices';
import Field from '../../../basic-components/field';
import useMediaQuery from '../../../hooks/use-media-query';
import SelectField from '../../../basic-components/select-field';
import Icon from '../../../basic-components/icon';
import ShipTo from '../ship-to';
import useShippingServices from "../../../hooks/useShippingServices"
import { FormField, FieldValue, FieldError } from '../../../hocs/final-form';
import { assembleCustomProfileDataWithSite } from "../profile-utils"
import { validCostIsNumberNoSetstate } from './shipping-packet-prices';

import t from './locale';
import t1 from '../locale';
import cs from './styles.pcss';
import type {
  ShippingProfileFragment_domesticShippingServices as DomesticShippingService,
  ShippingProfileFragment_internationalShippingServices as InternationalShippingService,
} from '../../../typings/product-query.flow';
import { transformToDropdownOptions, getRegionNames } from '../profile-utils';
import useWhitelist from '../../../hooks/use-whitelist';


type Props = {
  field: string, // 这里的filed相当于一个唯一标识
  optionType: 'domestic' | 'international',
  priority: number,
  siteId: number,
  currency: string,
  currentService: Object,
  shippingServiceDictionary: Array<
    DomesticShippingService | InternationalShippingService
  >,
  globalDisabled?: boolean,
  onDelete?: (number) => void,
};

export const validNumberReg = /^\d+(\.\d+)?$/;

export default function ShippingPacket(props: Props): React.Node {
  const {
    field,
    optionType,
    form,
    priority,
    siteId,
    currency,
    currentService,
    setbtnErrMsg,
    profileId,
    shippingServiceDictionary,
    globalDisabled = true,
    onDelete: removeShipping,
  } = props;
  // console.log("ShippingPacket------")

  const veryLargeScreen = useMediaQuery(`(min-width: 1280px)`);
  const isMobileScreen = useMediaQuery(`(max-width: 768px)`);
  const firstDomesticShipping = optionType === 'domestic' && priority === 0;



  // 显示可选service
  const dropdownOptions = transformToDropdownOptions(shippingServiceDictionary);
  // currentService有可能是空对象
  const {
    shippingServiceCode,
    freeShipping,
    shippingCost,
    additionalShippingCost,
    // 国内运送没有ship to
    shipToLocations = null,
  } = currentService;

  const [selectedServiceValue, setSelectedServiceValue] = React.useState(
    shippingServiceCode || ''
  );

  const firstCostRef = useRef(null); //cache first cost, otherwise it will be initialized after each render
  const additionalCostRef = useRef(null);//also cache it
  // fs: freeShipping, 只有国内运送才能设置free shipping
  const [fsChecked, setFsChecked] = React.useState(freeShipping || false);
  const [firstCost, setFirstCost] = React.useState(shippingCost?.value || 0);
  const [additionalCost, setAdditionalCost] = React.useState(
    additionalShippingCost?.value || 0
  );
  const [costInputErrorTip, setCostInputErrorTip] = React.useState('');
  const [costInputErrorTipPrice, setCostInputErrorTipPrice] = React.useState('');
  const [costInputErrorTipAddition, setCostInputErrorTipAddition] = React.useState('');
  const [shipToErrorTip, setShipToErrorTip] = React.useState('');
  const { whitelist: whitelist2, fetchWhitelist } = useWhitelist();

  useEffect(() => {
    if (firstCostRef.current == null) {

      // part 1
      // set an initial value for the first cost field and update this field value to the form
      // if not so, errors may not be noticed by the FormSpy if this field is not touched

      // You can test this by the below steps:
      // Comment part1 and part2, 
      // fill an amount in first cost, 
      // fill a bigger amount in additional cost field,
      // and then click Apply Changes submit button,
      // you will find out that
      // the first cost field and additional cost field will not be marked as errored(i.e. the field will not display a red underline).

      // That's why I add part 1 and part 2 here.
      firstCostRef.current = { field: `${field}.cost.price`, value: shippingCost?.value || 0 }
      form?.change(`${field}.cost.price`, shippingCost?.value || 0)
    }

    if (additionalCostRef.current == null) {
      // part 2
      // also initialize the additional cost field
      additionalCostRef.current = { field: `${field}.cost.additional`, value: additionalShippingCost?.value || 0 }
      form?.change(`${field}.cost.additional`, additionalShippingCost?.value || 0)
    }

    if (firstCostRef.current != null && typeof firstCostRef.current === 'object') {
      // part 3
      // update the form field value when onblur
      form?.change(firstCostRef.current.field, firstCostRef.current.value)
    }
    if (additionalCostRef.current != null) {
      // part 4
      form?.change(additionalCostRef.current.field, additionalCostRef.current.value)
    }
  }, [firstCost, additionalCost])


  const { domesticShippingServices: domesticShippingServices2, internationalServices: internationalServices2 } = useShippingServices()


  const dropdownOptionsDs = transformToDropdownOptions(domesticShippingServices2?.filter(item => item.site_id == siteId) || []);
  const dropdownOptionsIs = transformToDropdownOptions(internationalServices2?.filter(item => item.site_id == siteId) || []);

  // cost: type=1
  // addition:type=2
  const validCostIsNumber = (costInput: string, type): boolean => {
    if (validNumberReg.test(costInput)) {
      if (type == 1) {
        setCostInputErrorTipPrice('');
      }
      else {
        setCostInputErrorTipAddition('');
      }

      return true;
    }

    if (type == 1) {
      setCostInputErrorTipPrice(t('price.validate.notNumber'));
    }
    else {
      setCostInputErrorTipAddition(t('price.validate.notNumber'));
    }
    setCostInputErrorTip('');
    return false;
  };

  const validateFirstCostLowerThanAdditionalCost = (
    first: number | string,
    additional: number | string
  ): boolean => {
    if ((!validCostIsNumberNoSetstate(first)) || (!validCostIsNumberNoSetstate(additional))) {
      setCostInputErrorTip('');
      return true;
    }
    const firstParsed = parseFloat(first);
    // console.log("🚀 ~ file: index.js:155 ~ firstParsed:", firstParsed)
    const additionalParsed = parseFloat(additional);
    // console.log("🚀 ~ file: index.js:157 ~ additionalParsed:", additionalParsed)
    if (firstParsed < additionalParsed) {
      setCostInputErrorTip(t('price.validate.additionalHigher'));
      return false;
    }
    setCostInputErrorTip('');
    return true;
  };


  const validatePriceIsNotSmallerThanAdditionalPrice = (customProfiles) => {
    let priceIsSmallerThanAdditionalResArr = []
    let errorMsg = null

    for (const customProfile of customProfiles) {
      let { domsEbayTariffs, intlEbayTariffs, site } = customProfile
      // console.log("🚀 ~ file: index.js:173 ~ domsEbayTariffs:", domsEbayTariffs)

      // Only pass the error for the current cross-site tab(ignore the tab which is not currently setOpened)
      // console.log("🚀 ~ file: index.js:247 ~ field?.split('-')?.[2]:", field?.split('-')?.[2])
      let countryCodeOfField = field?.split('-')?.[2]
      // 310-cp-fr-ds-0
      // site: "au"

      // first go through domsEbayTariffs and find if there's any record in which price is smaller than additionalPrice
      if (domsEbayTariffs && domsEbayTariffs instanceof Array && domsEbayTariffs.length > 0) {
        for (const domsEbayTariff of domsEbayTariffs) {
          let { shippingCost: price, additionalShippingCost: additionalPrice } = domsEbayTariff
          let element = `${site}-domestic`
          if ((price - 0) < (additionalPrice - 0)) {
            if (!priceIsSmallerThanAdditionalResArr.includes(element)) {
              priceIsSmallerThanAdditionalResArr.push(element)
            }
            if (countryCodeOfField == site) {
              errorMsg = "errors come up"
            }
          }

        }
      }

      // then go through intlEbayTariffs and do the same
      if (intlEbayTariffs && intlEbayTariffs instanceof Array && intlEbayTariffs.length > 0) {
        for (const intlEbayTariff of intlEbayTariffs) {
          let { shippingCost: price, additionalShippingCost: additionalPrice } = intlEbayTariff
          let element = `${site}-international`
          if ((price - 0) < (additionalPrice - 0)) {
            if (!priceIsSmallerThanAdditionalResArr.includes(element)) {
              priceIsSmallerThanAdditionalResArr.push(element)
            }
            if (countryCodeOfField == site) {
              errorMsg = "errors come up"
            }
          }
        }
      }
    }
    if (priceIsSmallerThanAdditionalResArr?.length > 0) {
      setbtnErrMsg(priceIsSmallerThanAdditionalResArr.join(",") + "   " + t('price.validate.additionalHigher'))
    } else {
      setbtnErrMsg("")
    }
    return errorMsg
  }

  const validatePriceAndAdditionalPriceIsNumber = (customProfiles) => {
    let illegalArr = []
    let errorMsg = null
    for (const customProfile of customProfiles) {
      let { domsEbayTariffs, intlEbayTariffs, site } = customProfile
      // console.log("🚀 ~ file: index.js:173 ~ domsEbayTariffs:", domsEbayTariffs)
      // Only pass the error for the current cross-site tab(ignore the tab which is not currently setOpened)
      // console.log("🚀 ~ file: index.js:247 ~ field?.split('-')?.[2]:", field?.split('-')?.[2])
      let countryCodeOfField = field?.split('-')?.[2]
      // 310-cp-fr-ds-0
      // site: "au"
      // first go through domsEbayTariffs and find if there's any record in which price is smaller than additionalPrice
      if (domsEbayTariffs && domsEbayTariffs instanceof Array && domsEbayTariffs.length > 0) {
        for (const domsEbayTariff of domsEbayTariffs) {
          let { shippingCost: price, additionalShippingCost: additionalPrice } = domsEbayTariff
          let element = `${site}-domestic`
          if ((validCostIsNumberNoSetstate(price + '') === false) || (validCostIsNumberNoSetstate(additionalPrice + '') === false)) {
            if (!illegalArr.includes(element)) {
              illegalArr.push(element)
            }
            if (countryCodeOfField == site) {
              errorMsg = "errors come up"
            }
          }

        }
      }

      // then go through intlEbayTariffs and do the same
      if (intlEbayTariffs && intlEbayTariffs instanceof Array && intlEbayTariffs.length > 0) {
        for (const intlEbayTariff of intlEbayTariffs) {
          let { shippingCost: price, additionalShippingCost: additionalPrice } = intlEbayTariff
          let element = `${site}-international`
          if ((validCostIsNumberNoSetstate(price + '') === false) || (validCostIsNumberNoSetstate(additionalPrice + '') === false)) {
            if (!illegalArr.includes(element)) {
              illegalArr.push(element)
            }
            if (countryCodeOfField == site) {
              errorMsg = "errors come up"
            }
          }
        }
      }

    }

    return illegalArr
  }

  const handleFirstCostInputChange = (e, field) => {
    console.log("🚀 ~ file: index.js:301 ~ handleFirstCostInputChange:")
    const inputValue = e.target.value;
    setFirstCost(inputValue);
    form?.change(field, inputValue)
    firstCostRef.current = { field, value: inputValue }
    let res = validCostIsNumber(inputValue, 1)
    if (res == true) {
      validateFirstCostLowerThanAdditionalCost(inputValue, additionalCost)
    }
  };

  const handleAdditionalCostInputChange = (e, field) => {
    const inputValue = e.target.value;
    setAdditionalCost(inputValue);
    form?.change(field, inputValue)
    additionalCostRef.current = { field, value: inputValue }
    let res = validCostIsNumber(inputValue, 2)
    if (res == true) {
      validateFirstCostLowerThanAdditionalCost(firstCost, inputValue)
    }
  };

  const handleDelete = () => {
    if (removeShipping) {
      removeShipping(priority);
    }
  };

  const deleteButton = (
    <button
      type="button"
      className={cs.remove}
      onClick={handleDelete}
      disabled={globalDisabled}
    >
      <Icon name="trash" />
    </button>
  );

  const handleShipToError = (errorTip) => {
    setShipToErrorTip(errorTip);
  };

  // 这里不用对currentSerivce验证是否falsy，默认是空对象。
  // 如果是空对象，说明卖家点击ADD按钮准备添加新的shipping

  return (
    <FieldSet delimiter="narrow" >
      {/* <h1 style={{color:'red'}}>field:{field}</h1> */}
      <SelectField
        searchable
        fieldClassName={cs.fieldClassName}
        key={`${field}.service`}
        label={t('service.label')}
        options={whitelist2 ? (optionType === 'domestic' ? dropdownOptionsDs : dropdownOptionsIs) : dropdownOptions}
        value={selectedServiceValue}
        onChange={(option) => setSelectedServiceValue(option?.value || '')}
        disabled={globalDisabled}
        description={!firstDomesticShipping && deleteButton}
      />
      <input
        type="hidden"
        name={`${field}.service`}
        value={selectedServiceValue}
      />

      {/* display shipping prices */}
      <>
        {veryLargeScreen && (
          <div className={cs.pricingHeader}>
            <div className={cs.pricingHeaderItem}>{t('price.first')}</div>
            <div className={cs.pricingHeaderItem}>{t('price.additional')}</div>
          </div>
        )}

        {/* <h1 style={{color:'gold'}}>field:{field}</h1> */}

        <FieldError name={`${field}`} >
          {(shippingPacketError) => {
            return (
              <>
                <FormField
                  name={`${field}`}
                  Component={ShippingPacketPrices}
                  error={shippingPacketError}
                  label={t('price.label', {
                    currency,
                    currencySymbol: getCurrencySymbol(currency),
                  })}
                  fieldClassName={cs.fieldClassName}
                  description={t('price.description')}
                  firstCost={firstCost}
                  setFirstCost={setFirstCost}
                  additionalCost={additionalCost}
                  setAdditionalCost={setAdditionalCost}
                  fsChecked={fsChecked}
                  globalDisabled={globalDisabled}
                  priceFirstLabel={t1('fields.price.first')}
                  priceAdditionalLabel={t1('fields.price.additional')}
                  veryLargeScreen={veryLargeScreen}
                  costInputErrorTip={costInputErrorTip}
                  costInputErrorTipPrice={costInputErrorTipPrice}
                  costInputErrorTipAddition={costInputErrorTipAddition}
                  handleFirstCostInputChange={handleFirstCostInputChange}
                  handleAdditionalCostInputChange={handleAdditionalCostInputChange}
                  field={field}
                  validate={(val) => {
                    // It the inputParams don't pass this validate function, the onSubmit method of the form will not be fired.
                    // You can test this by inputing some illegal combination of price and additional price, and then console.log something in the onSubmit method, 
                    // you will find that nothing is printed.
                    // for example, put the below line in the onSubmit method
                    // console.log("🚀 ~ file: form.js:458 ~ data:", data)
                    const customProfiles = assembleCustomProfileDataWithSite(profileId);
                    let illegalArr = validatePriceAndAdditionalPriceIsNumber(customProfiles)
                     if (illegalArr?.length > 0) {
                      setbtnErrMsg(illegalArr.join(",") + "   " + t('price.validate.notNumber'))
                      return illegalArr
                    } else {
                      setbtnErrMsg("")
                    }
                    
                    let err2 = validatePriceIsNotSmallerThanAdditionalPrice(customProfiles)
                    if (err2 != null) {
                      return err2
                    }
                    return null;

                  }}
                />
              </>




            )
          }}
        </FieldError>






        {/* 是否是free shipping，ebay规定只能有一个free shipping */}
        {firstDomesticShipping && (
          <Field
            label={t('free.label')}
            fieldClassName={cs.fieldClassName}
            noGrid={!veryLargeScreen}
          >
            <Checkbox
              key={`${field}.cost.free`}
              name={`${field}.cost.free`}
              checked={fsChecked}
              disabled={globalDisabled}
              onChange={(e) => {
                setFsChecked(e.target.checked);
                setFirstCost(0);
                setAdditionalCost(0);
                setCostInputErrorTip('');
              }}
            />
          </Field>
        )}
      </>

      {/* 国际运送显示Ship To */}
      {optionType === 'international' && (
        <Field
          label={t('shipTo.label')}
          error={shipToErrorTip}
          fieldClassName={cs.fieldClassName}
        >
          <ShipTo
            key={`${field}.shipto`}
            name={`${field}.shipto`}
            siteId={siteId}
            selectedLocations={getRegionNames(shipToLocations?.regionIncluded)}
            disabled={globalDisabled}
            onError={handleShipToError}
          />
        </Field>
      )}
    </FieldSet>
  );
}
